import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { IntlManager, SiteConfigManager, useApplicationState } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import {
  Button,
  HEADER_LAYOUT_TYPES,
  Icon,
  Label,
  SeparateBalance,
  useHeaderContext,
} from '@atlas/ui';

import HeaderNonAutorized from 'components/Header/HeaderNonAutorized';
import LangSwitch from 'components/LangSwitch/LangSwitch';

import SmarticoInboxButton from './SmarticoInboxButton';
import { getDepositLink, getHeaderContentClassName } from './utils';

const { CLIENT_PROFILE } = COMPONENT_TYPES;

const HeaderContent = () => {
  const layout = useHeaderContext();
  const {
    account,
    authenticated,
    wallet: { detailedBalance: { BONUS = 0, CASH = 0, FROZEN = 0 } = {} } = {},
  } = useApplicationState();

  const isSeparateBalance = SiteConfigManager.getConfig(
    'config.config.general.mobileSeparateRealAndBonusBalances',
  );

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const depositLink = getDepositLink(account, clientProfilePath);

  const decimalBalance = IntlManager.formatMoney(CASH + FROZEN);
  const decimalBonus = typeof BONUS === 'number' ? IntlManager.formatMoney(Number(BONUS)) : null;
  const regex = /^(\D*)?([\d.,\s]+)(\D*)?$/;
  const match = decimalBalance.match(regex);
  const bonusMatch = decimalBonus.match(regex);
  const isAmountFirst = decimalBalance.match(/^\d/);

  const currency = (match[1] || match[3] || '').trim();
  const amount = match[2];
  const bonusCurrency = (bonusMatch[1] || bonusMatch[3] || '').trim();
  const bonusAmount = bonusMatch[2];

  const renderBalance = useMemo(() => {
    return (
      <div className="title-small-bold-text">
        {isAmountFirst ? (
          <>
            <span className="amount">{amount}</span>
            <span className="balance-currency">{currency}</span>
          </>
        ) : (
          <>
            <span className="balance-currency">{currency}</span>
            <span className="amount">{amount}</span>
          </>
        )}
      </div>
    );
  }, [amount, currency, isAmountFirst]);

  const renderBonus = useMemo(() => {
    return isAmountFirst ? (
      <>
        <span className="amount">{bonusAmount}</span>
        <span className="balance-currency">{bonusCurrency}</span>
      </>
    ) : (
      <>
        <span className="balance-currency">{bonusCurrency}</span>
        <span className="amount">{bonusAmount}</span>
      </>
    );
  }, [bonusAmount, bonusCurrency, isAmountFirst]);

  const showSignUpButton =
    SiteConfigManager.getConfig('config.config.enable_registration') &&
    SiteConfigManager.getConfig('config.config.mobile_header_authorization_view') ===
      'SIGN_IN_WITH_SIGN_UP';

  const headerContentClassName = getHeaderContentClassName(
    layout,
    authenticated,
    showSignUpButton,
    isSeparateBalance,
  );

  return (
    <div className="header-content-wrapper">
      <SmarticoInboxButton />
      <div className={headerContentClassName}>
        {authenticated ? (
          <>
            <Link
              className={`balance-container ${layout === HEADER_LAYOUT_TYPES.HEADER6 ? 'header-6-balance' : ''}`}
              to={clientProfilePath}
            >
              {layout === HEADER_LAYOUT_TYPES.HEADER6 && (
                <div className="user-icon">
                  <Icon name="user-icon" />
                </div>
              )}
              {isSeparateBalance ? (
                <SeparateBalance
                  balance={renderBalance}
                  bonus={<div className="title-small-bold-text">{renderBonus}</div>}
                  className="balance-label"
                  isMobile
                >
                  {renderBalance}
                </SeparateBalance>
              ) : (
                <div className="balance-label">
                  {renderBalance}
                  <span className="sub-title-small-300-text">
                    <Label message="bonus_balance_header" />
                    &nbsp;
                    {renderBonus}
                  </span>
                </div>
              )}
            </Link>
            <Link className="deposit-button-container" to={depositLink}>
              {layout === HEADER_LAYOUT_TYPES.HEADER5 || layout === HEADER_LAYOUT_TYPES.HEADER6 ? (
                <Icon name={layout === HEADER_LAYOUT_TYPES.HEADER6 ? 'wallet' : 'money'} />
              ) : (
                <Button block label="mobile_deposit" size="medium" type="primary" />
              )}
            </Link>
          </>
        ) : (
          <>
            <LangSwitch isSmall />
            <HeaderNonAutorized twoButton={showSignUpButton} />
          </>
        )}
      </div>
    </div>
  );
};

export default HeaderContent;
